import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { PreferenceKey, GetUserPreferenceResponse } from '@erp/rpc';

export function useUserPreference(key: PreferenceKey) {
  return useQuery(
    orpc.core.userPreferences.get.queryOptions({
      input: { key },
    }),
  );
}

// Shared key so overlapping preference upserts are counted together (and apart
// from unrelated mutations) when deciding which one reconciles on settle.
const UPSERT_PREFERENCE_MUTATION_KEY = [
  'core',
  'userPreferences',
  'upsert',
] as const;

export function useUpsertUserPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: UPSERT_PREFERENCE_MUTATION_KEY,
    mutationFn: rpcClient.core.userPreferences.upsert,
    // Apply the change to the cache immediately so the UI (sidebar + settings
    // page) updates without waiting for the server round-trip — drag reorders,
    // hiding, and renames feel instant. Rolled back on error.
    onMutate: async (variables) => {
      const queryKey = orpc.core.userPreferences.get.queryKey({
        input: { key: variables.key },
      });
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<GetUserPreferenceResponse>(queryKey);
      queryClient.setQueryData<GetUserPreferenceResponse>(queryKey, {
        key: variables.key,
        value: variables.value,
      });
      return { queryKey, previous };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSuccess: (data, variables) => {
      const queryKey = orpc.core.userPreferences.get.queryKey({
        input: { key: variables.key },
      });
      queryClient.setQueryData<GetUserPreferenceResponse>(queryKey, {
        key: data.key,
        value: data.value,
      });
    },
    onSettled: (_data, _error, _variables, context) => {
      // Once the last in-flight preference upsert settles, reconcile with the
      // server so a late or erroring mutation can't leave stale optimistic
      // state (its onError rollback would otherwise restore a snapshot older
      // than a newer successful update). Scope the count to this mutation key
      // so unrelated in-flight mutations don't block the reconcile, and so only
      // the final upsert invalidates (avoids refetch churn during rapid drags).
      const pending = queryClient.isMutating({
        mutationKey: UPSERT_PREFERENCE_MUTATION_KEY,
        exact: true,
      });
      if (context && pending <= 1) {
        void queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
  });
}
